import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

const TermsOfService: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Box
        sx={{
          background: (theme) =>
            `linear-gradient(160deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
          py: 8,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 3 }}>
            Legal
          </Typography>
          <Typography variant="h2" component="h1" sx={{ my: 2, fontWeight: 700 }}>
            {t('terms.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
            {t('terms.lastUpdated')}. Please read these terms carefully before using IndeeClips.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          By accessing or using IndeeClips ("the Platform"), you agree to be bound by these Terms of Service. If you do
          not agree with any part of these terms, you may not use our platform.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          1. Acceptance of Terms
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          By creating an account or using IndeeClips in any way, you confirm that you are at least 13 years of age and
          that you agree to these Terms of Service and our Privacy Policy. If you are using IndeeClips on behalf of a
          company or organisation, you represent that you have the authority to bind that entity to these terms.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          2. Use of the Platform
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips grants you a limited, non-exclusive, non-transferable licence to access and use our platform for
          personal, non-commercial purposes. You agree not to:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Use the platform for any unlawful or unauthorised purpose</li>
          <li>Upload, post or share content that infringes any intellectual property rights</li>
          <li>Upload content that is defamatory, obscene, abusive or harmful</li>
          <li>Attempt to gain unauthorised access to our systems or networks</li>
          <li>Use automated tools to scrape, crawl or extract data from our platform</li>
          <li>Distribute spam, malware or any harmful software through our platform</li>
          <li>Impersonate any person or entity or misrepresent your affiliation</li>
        </Box>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          3. Content and Copyright
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Users retain ownership of content they upload to IndeeClips. By uploading content, you grant IndeeClips a
          worldwide, non-exclusive, royalty-free licence to host, store, reproduce, modify and distribute your content
          solely for the purpose of operating and improving our platform.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          You represent and warrant that you own or have the necessary rights to upload any content you post, and that
          your content does not violate any third party rights.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          4. Prohibited Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The following types of content are strictly prohibited on IndeeClips:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Content that infringes copyright, trademark or other intellectual property rights</li>
          <li>Sexually explicit or pornographic content</li>
          <li>Content that depicts or promotes violence, self-harm or illegal activities</li>
          <li>Hate speech targeting any individual or group</li>
          <li>Content involving the exploitation of minors</li>
          <li>Misleading, fraudulent or deceptive content</li>
          <li>Spam or commercially deceptive content</li>
        </Box>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          5. Account Termination
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          We reserve the right to suspend or terminate your account at any time if you violate these Terms of Service.
          We may also remove any content that violates our policies without prior notice.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          6. Advertising
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          IndeeClips is a free platform supported by advertising revenue. By using our platform, you agree that we may
          display advertisements within our app and website. Advertisers are responsible for the accuracy of their ads,
          and we are not liable for any content displayed in advertisements.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          7. Disclaimer of Warranties
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          IndeeClips is provided "as is" without warranties of any kind, either express or implied. We do not guarantee
          that the platform will be uninterrupted, error-free or secure. You use the platform at your own risk.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          8. Limitation of Liability
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          To the maximum extent permitted by law, IndeeClips shall not be liable for any indirect, incidental, special
          or consequential damages arising from your use of the platform, even if we have been advised of the
          possibility of such damages.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          9. Changes to Terms
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          We may update these Terms of Service from time to time. We will notify you of significant changes by posting
          the updated terms on this page with a revised date. Your continued use of IndeeClips after any changes
          constitutes your acceptance of the updated terms.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          10. Governing Law
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          These Terms of Service shall be governed by and construed in accordance with the laws of India. Any disputes
          arising from these terms shall be subject to the exclusive jurisdiction of the courts in India.
        </Typography>

        <Typography variant="h4" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          11. Contact Us
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, lineHeight: 1.85 }}>
          If you have any questions about these Terms of Service, please contact us at:
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.85 }}>
          Email:{' '}
          <Typography
            component="a"
            href="mailto:contact@indeeclips.com"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            contact@indeeclips.com
          </Typography>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
          Website:{' '}
          <Typography
            component="a"
            href="https://indeeclips.com"
            target="_blank"
            rel="noreferrer"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            https://indeeclips.com
          </Typography>
        </Typography>
      </Container>
    </Box>
  );
};

export default TermsOfService;
